import React, { useState } from 'react';
import {
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@material-ui/core';
import i18n from 'i18n-js';
import DateFilters, {
    DateFilterType,
} from 'components/restaurantInformation/finance/components/DateFIlters';
import { useStyles } from '../styles';
import agent from 'api/agent';
import { formatDateForServer } from 'helpers/helperFunctions';
import { SortOrder } from 'constants/types';
import { DishStock, IdName } from 'api/models';
import { useDispatch } from 'react-redux';
import globalStore from 'store/globalStore';
import { MultiSelectAutocomplete, NothingFoundMessage, SearchInput } from 'components/shared';
import { images } from 'assets';
import clsx from 'clsx';
import { StockTableSearchFilter } from '../interfaces';

interface RestaurantDishStock extends DishStock {
    restaurantId: string;
    restaurantName: string;
}

export default function RestaurantStockTab() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [dateFilter, setDateFilter] = useState(DateFilterType.DAY);
    const [dishes, setDishes] = useState<RestaurantDishStock[]>([]);
    const [order, setOrder] = useState<SortOrder>('asc');
    const [orderBy, setOrderBy] = useState<keyof RestaurantDishStock | ''>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [restaurants, setRestaurants] = useState<IdName[]>([]);
    const [selectedRestaurants, setSelectedRestaurants] = useState<IdName[]>([]);
    const [filter, setFilter] = useState<StockTableSearchFilter>({
        name: '',
        externalId: '',
        supplierName: '',
    });

    function getData(from: Date, to: Date) {
        setIsLoading(true);
        agent.Restaurants.GetOwnerRestaurantsStock(
            formatDateForServer(from),
            formatDateForServer(to)
        )
            .then((res) => {
                const _dishes = res.reduce((acc, next) => {
                    for (let i = 0; i < next.products.length; i++) {
                        const dish = next.products[i];
                        acc.push({ ...dish, restaurantName: next.name, restaurantId: next.id });
                    }
                    return acc;
                }, [] as RestaurantDishStock[]);

                const restaurants = res.map((x) => ({ id: x.id, name: x.name }));
                setRestaurants(restaurants);
                setSelectedRestaurants(
                    selectedRestaurants.length ? selectedRestaurants : restaurants
                );
                setDishes(_dishes);
            })
            .catch((err) => {
                dispatch(
                    globalStore.actionCreators.showToaster('error', i18n.t('common.somethingWrong'))
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const handleRequestSort = (property: keyof RestaurantDishStock) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    function sortData(a: RestaurantDishStock, b: RestaurantDishStock) {
        if (!orderBy) return 0;
        const v1 = a[orderBy];
        const v2 = b[orderBy];
        let result = 0;
        const isEmpty1 = v1 === null || v1 === undefined || v1 === '';
        const isEmpty2 = v2 === null || v2 === undefined || v2 === '';

        if (isEmpty1 && isEmpty2) return 0;
        if (isEmpty1) return 1;
        if (isEmpty2) return -1;

        if (typeof v1 === 'string' && typeof v2 === 'string') {
            result = v1.localeCompare(v2);
        } else if (typeof v1 === 'number' && typeof v2 === 'number') {
            result = v1 - v2;
        }

        return order === 'asc' ? result : -result;
    }

    function normalize(value?: string | null) {
        return value?.trim().toLowerCase() || '';
    }

    function filterData(dish: RestaurantDishStock) {
        if (!selectedRestaurants.some((r) => r.id === dish.restaurantId)) {
            return false;
        }

        const name = normalize(filter.name);
        const externalId = normalize(filter.externalId);
        const supplierName = normalize(filter.supplierName);

        if (name && !normalize(dish.name).includes(name)) {
            return false;
        }
        if (externalId && !normalize(dish.externalId?.toString()).includes(externalId)) {
            return false;
        }
        if (supplierName && !normalize(dish.supplierName).includes(supplierName)) {
            return false;
        }

        return true;
    }

    return (
        <>
            <div className={classes.p15}>
                <DateFilters
                    dateFilter={dateFilter}
                    setDateFilter={setDateFilter}
                    getData={(_, __, from, to) => from && to && getData(from, to)}
                    selectedDatesHistory={[]}
                    setSelectedDatesHistory={() => {}}
                    showClosing={false}
                />
            </div>
            <div
                className={clsx(
                    classes.flexRowEnd,
                    classes.mBot15,
                    classes.mRight15,
                    classes.mLeft15
                )}
            >
                <MultiSelectAutocomplete
                    style={{ flex: '1 0 50%', maxWidth: 300 }}
                    name="selectedRestaurants"
                    options={restaurants}
                    selectedValues={selectedRestaurants}
                    getOptionLabel={(option) => option?.name ?? ''}
                    inputLabel={i18n.t('common.selectFromTheList')}
                    popupIcon={images.icons.triangleDown}
                    getOptionSelected={(option, value) => option?.id === value?.id}
                    onChange={(_, values) => {
                        setSelectedRestaurants(values);
                    }}
                    trimLength={30}
                    showSelectAll={true}
                />
            </div>

            <TableContainer>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sortDirection={orderBy === 'name' ? order : false}
                                width={300}
                                style={{ maxWidth: 300 }}
                            >
                                <div className={classes.flexRow}>
                                    <TableSortLabel
                                        className={classes.mRight15}
                                        active={orderBy === 'name'}
                                        direction={orderBy === 'name' ? order : 'asc'}
                                        onClick={() => handleRequestSort('name')}
                                    ></TableSortLabel>
                                    <div style={{ position: 'relative', bottom: 8 }}>
                                        <SearchInput
                                            fontSize={16}
                                            name={i18n.t('form.itemName')}
                                            value={filter.name}
                                            onChange={(val) => setFilter({ ...filter, name: val })}
                                            onFocus={() => {}}
                                            onEnter={() => ''}
                                            onReset={() => setFilter({ ...filter, name: '' })}
                                            trimValue={false}
                                        />
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell
                                sortDirection={orderBy === 'externalId' ? order : false}
                                width={150}
                                style={{ maxWidth: 150 }}
                            >
                                <div className={classes.flexRow}>
                                    <TableSortLabel
                                        className={classes.mRight15}
                                        active={orderBy === 'externalId'}
                                        direction={orderBy === 'externalId' ? order : 'asc'}
                                        onClick={() => handleRequestSort('externalId')}
                                    ></TableSortLabel>
                                    <div style={{ position: 'relative', bottom: 8 }}>
                                        <SearchInput
                                            fontSize={16}
                                            name={i18n.t('form.externalId')}
                                            value={filter.externalId}
                                            onChange={(val) =>
                                                setFilter({ ...filter, externalId: val })
                                            }
                                            onFocus={() => {}}
                                            onEnter={() => ''}
                                            onReset={() => setFilter({ ...filter, externalId: '' })}
                                            trimValue={false}
                                        />
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell
                                sortDirection={orderBy === 'supplierName' ? order : false}
                                width={200}
                                style={{ maxWidth: 200 }}
                            >
                                <div className={classes.flexRow}>
                                    <TableSortLabel
                                        className={classes.mRight15}
                                        active={orderBy === 'supplierName'}
                                        direction={orderBy === 'supplierName' ? order : 'asc'}
                                        onClick={() => handleRequestSort('supplierName')}
                                    ></TableSortLabel>
                                    <div style={{ position: 'relative', bottom: 8 }}>
                                        <SearchInput
                                            fontSize={16}
                                            name={i18n.t('form.supplierName')}
                                            value={filter.supplierName}
                                            onChange={(val) =>
                                                setFilter({ ...filter, supplierName: val })
                                            }
                                            onFocus={() => {}}
                                            onEnter={() => ''}
                                            onReset={() =>
                                                setFilter({ ...filter, supplierName: '' })
                                            }
                                            trimValue={false}
                                        />
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell
                                align="right"
                                sortDirection={orderBy === 'price' ? order : false}
                            >
                                <TableSortLabel
                                    active={orderBy === 'price'}
                                    direction={orderBy === 'price' ? order : 'asc'}
                                    onClick={() => handleRequestSort('price')}
                                >
                                    {i18n.t('common.price')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="right"
                                sortDirection={orderBy === 'sold' ? order : false}
                            >
                                <TableSortLabel
                                    active={orderBy === 'sold'}
                                    direction={orderBy === 'sold' ? order : 'asc'}
                                    onClick={() => handleRequestSort('sold')}
                                >
                                    {i18n.t('common.sold')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="right"
                                sortDirection={orderBy === 'stockQuantity' ? order : false}
                            >
                                <TableSortLabel
                                    active={orderBy === 'stockQuantity'}
                                    direction={orderBy === 'stockQuantity' ? order : 'asc'}
                                    onClick={() => handleRequestSort('stockQuantity')}
                                >
                                    {i18n.t('form.stockQuantity')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="right"
                                sortDirection={orderBy === 'minimumStock' ? order : false}
                            >
                                <TableSortLabel
                                    active={orderBy === 'minimumStock'}
                                    direction={orderBy === 'minimumStock' ? order : 'asc'}
                                    onClick={() => handleRequestSort('minimumStock')}
                                >
                                    {i18n.t('form.minimumStock')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="right"
                                sortDirection={orderBy === 'restaurantName' ? order : false}
                                width={150}
                                style={{ maxWidth: 150 }}
                            >
                                <TableSortLabel
                                    active={orderBy === 'restaurantName'}
                                    direction={orderBy === 'restaurantName' ? order : 'asc'}
                                    onClick={() => handleRequestSort('restaurantName')}
                                >
                                    {i18n.t('form.restaurantName')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {isLoading && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <CircularProgress size={48} className={classes.p15} />
                                </TableCell>
                            </TableRow>
                        )}
                        {!isLoading &&
                            dishes
                                .filter(filterData)
                                .sort(sortData)
                                .map((dish) => {
                                    return (
                                        <TableRow key={`${dish.restaurantId}-${dish.id}`}>
                                            <TableCell width={300} style={{ maxWidth: 300 }}>
                                                {dish.name}
                                            </TableCell>
                                            <TableCell width={150} style={{ maxWidth: 150 }}>
                                                {dish.externalId}
                                            </TableCell>
                                            <TableCell width={200} style={{ maxWidth: 200 }}>
                                                {dish.supplierName}
                                            </TableCell>
                                            <TableCell align="right">
                                                {dish.price.toFixed(2)}
                                            </TableCell>
                                            <TableCell align="right">{dish.sold}</TableCell>
                                            <TableCell align="right">
                                                {dish.stockQuantity}
                                            </TableCell>
                                            <TableCell align="right">{dish.minimumStock}</TableCell>
                                            <TableCell
                                                align="right"
                                                width={150}
                                                style={{ maxWidth: 150 }}
                                            >
                                                {dish.restaurantName}
                                            </TableCell>
                                            <TableCell align="right"></TableCell>
                                        </TableRow>
                                    );
                                })}
                        {!isLoading && dishes.filter(filterData).length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} style={{ height: 550 }}>
                                    <NothingFoundMessage />
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}
